// ** Redux Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { sessionService } from "./_shared/sessionService";
import { authLogout, resetAuthState } from "./auth";

export const sessionGetLocations = createAsyncThunk("session/getLocations", async (_, { rejectWithValue }) => {
  try {
    const response = await sessionService.getLocations({
      page: 1,
      rowsPerPage: 10000,
    });
    return response.data;
  } catch (err) {
    let errResp;
    if (err && err.response && err.response.data) {
      errResp = { error: err.response.data };
    } else {
      errResp = { error: err.toString() };
    }
    return rejectWithValue(errResp);
  }
});

export const startSession = createAsyncThunk("session/startSession", async (action, { rejectWithValue }) => {
  try {
    const response = await sessionService.startSession(action);
    return { ...response.data, playerId: action.playerId };
  } catch (err) {
    let errResp;
    if (err && err.response && err.response.data) {
      errResp = { error: err.response.data, playerId: action.playerId };
    } else {
      errResp = { error: err.toString(), playerId: action.playerId };
    }
    return rejectWithValue(errResp);
  }
});

export const sessionCheck = createAsyncThunk(
  "session/checkSession",
  async (action, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await sessionService.check(action);

      if (!response.data.active) {
        const session = getState().session;
        if (!(session.waitForCheck && !session.hasApprovedSession)) {
          dispatch(authLogout());
          dispatch(resetAuthState());
          dispatch(resetSessionState());
        }
      }

      return { ...response.data, playerId: action.playerId };
    } catch (err) {
      let errResp;
      if (err && err.response && err.response.data) {
        errResp = { error: err.response.data, playerId: action.playerId };
      } else {
        errResp = { error: err.toString(), playerId: action.playerId };
      }

      dispatch(authLogout());
      dispatch(resetAuthState());
      dispatch(resetSessionState());
      return rejectWithValue(errResp);
    }
  }
);

export const sessionSlice = createSlice({
  name: "session",
  initialState: {
    locationRequestActive: false,
    startSessionRequestActive: false,
    checkSessionRequestActive: false,
    hasStartedSession: localStorage.getItem("session-token") ? true : false,
    hasApprovedSession: false,
    locations: [],
    selectedLocation: localStorage.getItem("selected-location")
      ? JSON.parse(localStorage.getItem("selected-location"))
      : null,
    sessionToken: localStorage.getItem("session-token") ? localStorage.getItem("session-token") : null,
    sessionTokens: localStorage.getItem("session-tokens") ? JSON.parse(localStorage.getItem("session-tokens")) : null,

    proceedToStart: false,
    waitForCheck: false,
    error: "",
  },
  reducers: {
    resetSessionState: state => {
      state.locationRequestActive = false;
      state.startSessionRequestActive = false;
      state.checkSessionRequestActive = false;
      state.sessionToken = null;
      state.hasStartedSession = false;
      state.hasApprovedSession = false;
      //state.locations = [];
      //state.selectedLocation = null;
      state.waitForCheck = false;
      state.error = "";

      localStorage.removeItem("session-token");
      //localStorage.removeItem("selected-location");
    },
    setLocation(state, action) {
      state.selectedLocation = action.payload;
      if (state.selectedLocation) {
        localStorage.setItem("selected-location", JSON.stringify(state.selectedLocation));
      } else {
        localStorage.removeItem("selected-location");
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(sessionGetLocations.fulfilled, (state, action) => {
        state.locationRequestActive = false;
        state.error = "";

        state.locations = action.payload.data.rows;

        if (action.payload.data.rows.length === 0) {
          state.error = "No locations associated with this user";
        } else if (action.payload.data.rows.length === 1) {
          state.selectedLocation = action.payload.data.rows[0].id;
          state.proceedToStart = true;
        }
      })
      .addCase(sessionGetLocations.pending, state => {
        state.locationRequestActive = true;
        state.error = "";
      })
      .addCase(sessionGetLocations.rejected, (state, action) => {
        state.locationRequestActive = false;

        if (action && action.payload && action.payload.error && action.payload.error.message) {
          state.error = action.payload.error.message;
        } else if (action && action.error && action.error.message) {
          state.error = action.error.message;
        } else {
          state.error = "Unknown error";
        }
      })
      .addCase(startSession.fulfilled, (state, action) => {
        state.startSessionRequestActive = false;

        if (action.payload && action.payload.session_id) {
          state.sessionToken = action.payload.session_id;
          state.hasStartedSession = true;
          localStorage.setItem("session-token", action.payload.session_id);

          const currentSessions = localStorage.getItem("session-tokens");

          try {
            let sessions = {};
            if (currentSessions) {
              const tmp = JSON.parse(currentSessions);
              if (tmp !== null) {
                sessions = tmp;
              }
            }

            sessions[action.payload.playerId] = action.payload.session_id;
            state.sessionTokens = sessions;
            localStorage.setItem("session-tokens", JSON.stringify(sessions));
          } catch (e) {}
        }
      })
      .addCase(startSession.pending, state => {
        state.startSessionRequestActive = true;
        state.error = "";
      })
      .addCase(startSession.rejected, (state, action) => {
        state.startSessionRequestActive = false;
        if (action && action.payload && action.payload.error && action.payload.error.message) {
          state.error = action.payload.error.message;
        } else if (action && action.error && action.error.message) {
          state.error = action.error.message;
        } else {
          state.error = "Unknown error";
        }
      })
      .addCase(sessionCheck.fulfilled, (state, action) => {
        state.checkSessionRequestActive = false;
        if (action && action.payload && action.payload.active) {
          if (action.payload.approved) {
            state.hasStartedSession = true;
            state.hasApprovedSession = true;
            state.waitForCheck = false;
          } else {
            state.hasStartedSession = true;
            state.hasApprovedSession = false;
            state.waitForCheck = true;
          }
          state.sessionToken = action.payload.session_id;

          if (action.payload.location) {
            state.selectedLocation = action.payload.location;
            localStorage.setItem("selected-location", JSON.stringify(action.payload.location));
          }
        } else {
          state.hasStartedSession = false;
          state.hasApprovedSession = false;
          state.sessionToken = null;
          state.waitForCheck = false;
          localStorage.removeItem("session-token");

          const currentSessions = localStorage.getItem("session-tokens");
          try {
            let sessions = {};
            if (currentSessions) {
              const tmp = JSON.parse(currentSessions);
              if (tmp !== null) {
                sessions = tmp;
              }
            }

            delete sessions[action.payload.playerId];
            state.sessionTokens = sessions;
            localStorage.setItem("session-tokens", JSON.stringify(sessions));
          } catch (e) {}
        }
      })
      .addCase(sessionCheck.pending, state => {
        state.checkSessionRequestActive = true;
        state.error = "";
      })
      .addCase(sessionCheck.rejected, (state, action) => {
        state.checkSessionRequestActive = false;
        if (action && action.payload && action.payload.error && action.payload.error.message) {
          state.error = action.payload.error.message;
        } else if (action && action.error && action.error.message) {
          state.error = action.error.message;
        } else {
          state.error = "Unknown error";
        }
        localStorage.removeItem("session-token");

        const currentSessions = localStorage.getItem("session-tokens");
        try {
          let sessions = {};
          if (currentSessions) {
            const tmp = JSON.parse(currentSessions);
            if (tmp !== null) {
              sessions = tmp;
            }
          }

          delete sessions[action.payload.playerId];
          state.sessionTokens = sessions;
          localStorage.setItem("session-tokens", JSON.stringify(sessions));
        } catch (e) {}
      });
  },
});

export const { resetSessionState, setLocation } = sessionSlice.actions;

export default sessionSlice.reducer;
